import { accounting, accounting0 } from './money';
/**
 * Row-wash P&L statement — a single-column, single-page A4 portrait export.
 *
 * Each line is tinted from the left in proportion to its share of revenue, so
 * the row itself carries the weight and no chart column is needed. Drawn with
 * jsPDF primitives rather than autoTable: the wash sits behind the text, the
 * subtotal rules differ per row kind, and the composition footer is not a table
 * at all, none of which autoTable expresses cleanly.
 *
 * Deliberately free of React and CSS imports so it can be exercised directly
 * in node — see scripts that render it to an image for review.
 */

/* Palette — mirrors the approved mockup (mockup-pnl-pdf-c.html) */
const INK = [27, 26, 21];
const MUTE = [114, 108, 96];
const FAINT = [157, 150, 138];
const RULE = [220, 215, 201];
const HAIR = [239, 236, 227];
const AMBER = [176, 111, 0];
const WASH = [232, 221, 203];
const GOOD = [47, 107, 70];
const BAD = [168, 51, 31];

/* Page geometry (A4 portrait, points) */
const PAGE_W = 595.28;
const PAGE_H = 841.89;
const M_LEFT = 42;
const M_RIGHT = 42;
const M_TOP = 40;
const M_BOTTOM = 34;
const PCT_RIGHT = PAGE_W - M_RIGHT;
const PCT_W = 34;
const AMT_RIGHT = PCT_RIGHT - PCT_W - 8;

/* Lines whose result is a headline, not just a subtotal */
const KEY_KEYS = new Set(['gross_profit', 'ebitda', 'pbt']);
const FINAL_KEY = 'pat';

/* Sections whose lines are costs, where a rise is bad. The headline results
   (Gross Profit, EBITDA, PBT, PAT) sit inside these but are income-like, so
   they are excepted — see direction(). */
const COST_SECTIONS = new Set(['cogs', 'profit_sharing', 'opex', 'below_ebitda']);

/**
 * +1 when a rise in this line is good for profit, -1 when it is bad. Drives
 * both the red/green of the change columns and the sign of the points movement,
 * so "better" is always positive and always green.
 */
function direction(sectionKey, rowKey) {
  if (rowKey === FINAL_KEY || KEY_KEYS.has(rowKey)) return 1;
  return COST_SECTIONS.has(sectionKey) ? -1 : 1;
}

/* The composition footer: what each ringgit of revenue became */
const COMPOSITION = [
  { key: 'total_cogs', label: 'Cost of goods', rgb: [154, 123, 79] },
  { key: 'total_profit_sharing', label: 'Profit sharing', rgb: [201, 162, 39] },
  { key: 'total_opex', label: 'Operating expenses', rgb: [125, 106, 82] },
  { key: 'total_below_ebitda', label: 'Interest & depreciation', rgb: [188, 174, 156] },
  { key: 'pat', label: 'Kept as profit', rgb: [47, 107, 70] },
];

/* jsPDF's standard fonts are WinAnsi; em dashes and middots have been observed
   to drop out silently, so everything is folded to ASCII before drawing. */
function ascii(s) {
  return String(s == null ? '' : s)
    .replace(/[‒-―]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/·/g, '-')
    .replace(/[^\x20-\x7E]/g, '');
}

/**
 * Drop a label's own section name when it merely repeats the heading above it,
 * and demote any remaining dash to a comma: inside a section headed PROFIT
 * SHARING, "Profit Sharing - W Timber" is just "W Timber".
 */
export function tidyLabel(label, sectionName) {
  let s = String(label == null ? '' : label);
  if (sectionName) {
    const lead = s.slice(0, sectionName.length).toLowerCase();
    if (lead === sectionName.toLowerCase()) {
      const rest = s.slice(sectionName.length);
      if (/^\s[‒-―-]\s/.test(rest)) s = rest.replace(/^\s[‒-―-]\s/, '');
    }
  }
  return s.replace(/\s[‒-―]\s/g, ', ');
}

// Number(v) first, deliberately: this document has always read '' as 0 rather
// than as a blank, and the shared helper would call it empty.
function money(v) {
  return accounting(v == null ? null : Number(v), '-');
}
function pctText(v) {
  return v == null ? '-' : (Number(v) * 100).toFixed(1) + '%';
}

/**
 * Classify a row for styling. Section bands and the headline results are
 * distinguished by key rather than by kind, since the config marks Gross Profit
 * and Total COGS both as 'computed'.
 */
function classify(row) {
  if (row.kind === 'section') return 'section';
  if (row.key === FINAL_KEY) return 'final';
  if (KEY_KEYS.has(row.key)) return 'key';
  if (row.kind === 'computed') return 'total';
  return 'item';
}

/**
 * Build the printable row list: nil detail lines are held back (they carry no
 * information and each would cost a line of the page) and named once in a
 * footer note instead, so nothing disappears silently. Subtotals are always
 * kept even at zero, because a zero subtotal is itself a fact.
 */
export function buildStatementRows(rows, lookup) {
  const out = [];
  const omitted = [];
  let section = null;
  let pastFirstTotal = false;

  for (const row of rows) {
    if (row.kind === 'section') {
      section = row.label;
      pastFirstTotal = false;
      out.push({ type: 'section', label: row.label });
      continue;
    }
    const cell = lookup[row.key] || {};
    const type = classify(row);
    const isNil = cell.actual == null || Number(cell.actual) === 0;

    // Only the detail block above a section's first subtotal may be thinned.
    // Anything after it is structural — Tax sits between PBT and PAT at zero,
    // and dropping it would leave the two identical with nothing explaining why.
    if (type === 'item' && isNil && !pastFirstTotal) {
      omitted.push({ section, label: tidyLabel(row.label, section) });
      continue;
    }
    if (type !== 'item') pastFirstTotal = true;
    out.push({
      type,
      label: tidyLabel(row.label, section),
      amount: cell.actual == null ? null : Number(cell.actual),
      pct: cell.pctRev == null ? null : Number(cell.pctRev),
      indent: row.kind === 'child',
    });
  }

  // A section whose lines were all nil would otherwise print as a bare heading.
  const pruned = out.filter((r, i) => r.type !== 'section' || (out[i + 1] && out[i + 1].type !== 'section'));
  return { rows: pruned, omitted };
}

/** Height each row kind occupies, before any fitting adjustment. */
function rowHeight(type, unit) {
  if (type === 'section') return unit + 7;
  if (type === 'key') return unit + 3;
  if (type === 'final') return unit + 5;
  if (type === 'total') return unit + 2;
  return unit;
}

/**
 * Largest row unit that keeps the body inside `available`.
 *
 * Solved rather than scaled: each row is `unit + a constant for its kind`, and
 * those constants do not shrink with the unit. Scaling the unit by the ratio of
 * wanted-to-available overshoots by the whole constant term — which on a
 * thirteen-column year printed the footer note straight through the PAT row.
 */
function fitUnit(body, available, preferred, min) {
  if (!body.length) return preferred;
  const fixed = body.reduce((sum, r) => sum + rowHeight(r.type, 0), 0);
  const ideal = (available - fixed) / body.length;
  return Math.max(min, Math.min(preferred, ideal));
}

/**
 * Draw the statement. Returns the jsPDF instance for the caller to save.
 *
 * @param doc       a jsPDF instance, portrait A4, unit 'pt'
 * @param opts.org         organisation name for the eyebrow
 * @param opts.periodLabel e.g. 'July 2026'
 * @param opts.periodSub   e.g. 'Month ended 31 July 2026'
 * @param opts.generatedOn e.g. '13 Aug 2026'
 * @param opts.rows        output of buildRowList()
 * @param opts.lookup      output of buildColLookup()
 */
export function drawRowWashStatement(doc, opts) {
  const { org = 'Runningmen Group', periodLabel = '', periodSub = '', generatedOn = '' } = opts;
  const { rows: body, omitted } = buildStatementRows(opts.rows, opts.lookup);

  /* ---- header ---- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.6);
  doc.setTextColor(...AMBER);
  doc.text(ascii(org).toUpperCase(), M_LEFT, M_TOP + 10, { charSpace: 1.2 });

  doc.setFont('times', 'normal');
  doc.setFontSize(16);
  doc.setTextColor(...INK);
  doc.text('Profit & Loss Statement', M_LEFT, M_TOP + 28);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text(ascii(periodLabel), PCT_RIGHT, M_TOP + 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(...MUTE);
  doc.text(ascii(periodSub), PCT_RIGHT, M_TOP + 20, { align: 'right' });
  doc.text('All figures in RM', PCT_RIGHT, M_TOP + 29, { align: 'right' });

  /* ---- column caption ---- */
  let y = M_TOP + 46;
  doc.setFontSize(6);
  doc.setTextColor(...FAINT);
  doc.text('AMOUNT', AMT_RIGHT, y, { align: 'right', charSpace: 0.6 });
  doc.text('% REV', PCT_RIGHT, y, { align: 'right', charSpace: 0.6 });
  y += 6;

  /* ---- fit the body to one page ---- */
  const footerH = 96;
  const available = PAGE_H - M_BOTTOM - footerH - y;
  const unit = fitUnit(body, available, 12, 7.6);

  /* ---- body ---- */
  for (const row of body) {
    const h = rowHeight(row.type, unit);

    if (row.type === 'section') {
      y += h;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.6);
      doc.setTextColor(...AMBER);
      doc.text(ascii(row.label).toUpperCase(), M_LEFT, y - 3.5, { charSpace: 1.1 });
      doc.setDrawColor(...RULE);
      doc.setLineWidth(0.5);
      doc.line(M_LEFT, y, PCT_RIGHT, y);
      continue;
    }

    const top = y;
    y += h;
    const baseline = y - h * 0.28;

    // the wash: share of revenue, drawn behind everything else on the row
    if (row.pct != null && Math.abs(row.pct) > 0) {
      const frac = Math.min(1, Math.abs(row.pct));
      doc.setFillColor(...WASH);
      doc.rect(M_LEFT, top + 0.6, (PCT_RIGHT - M_LEFT) * frac, h - 1.2, 'F');
    }

    // rules above subtotals and the bottom line
    if (row.type === 'total' || row.type === 'key') {
      doc.setDrawColor(...RULE);
      doc.setLineWidth(0.5);
      doc.line(M_LEFT, top, PCT_RIGHT, top);
    } else if (row.type === 'final') {
      doc.setDrawColor(...INK);
      doc.setLineWidth(0.5);
      doc.line(M_LEFT, top, PCT_RIGHT, top);
      doc.line(M_LEFT, top + 1.8, PCT_RIGHT, top + 1.8);
    }

    // amber tick beside a headline result
    let labelX = M_LEFT + (row.indent ? 9 : 0);
    if (row.type === 'key') {
      doc.setFillColor(...AMBER);
      doc.rect(M_LEFT, top + 2.4, 1.8, h - 4.8, 'F');
      labelX = M_LEFT + 5;
    }

    const bold = row.type !== 'item';
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(row.type === 'final' ? 9 : 8);
    doc.setTextColor(...INK);
    doc.text(ascii(row.label), labelX, baseline);

    doc.setFont('courier', bold ? 'bold' : 'normal');
    doc.setFontSize(row.type === 'final' ? 8.6 : 7.7);
    if (row.amount != null && row.amount < 0) doc.setTextColor(...BAD);
    doc.text(money(row.amount), AMT_RIGHT, baseline, { align: 'right' });

    doc.setFont('courier', 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(...MUTE);
    doc.text(pctText(row.pct), PCT_RIGHT, baseline, { align: 'right' });
  }

  /* ---- composition strip ---- */
  const parts = COMPOSITION
    .map(p => ({ ...p, pct: (opts.lookup[p.key] || {}).pctRev }))
    .filter(p => p.pct != null && Number(p.pct) > 0);

  let fy = PAGE_H - M_BOTTOM - footerH + 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.6);
  doc.setTextColor(...AMBER);
  doc.text('WHERE EVERY RINGGIT OF REVENUE WENT', M_LEFT, fy, { charSpace: 1.1 });
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.5);
  doc.line(M_LEFT, fy + 3, PCT_RIGHT, fy + 3);

  fy += 7;
  const stripW = PCT_RIGHT - M_LEFT;
  let cx = M_LEFT;
  for (const p of parts) {
    const w = stripW * Math.min(1, Number(p.pct));
    doc.setFillColor(...p.rgb);
    doc.rect(cx, fy, w, 10, 'F');
    cx += w;
  }

  fy += 20;
  doc.setFontSize(6.4);
  let lx = M_LEFT;
  for (const p of parts) {
    doc.setFillColor(...p.rgb);
    doc.rect(lx, fy - 4.6, 5, 5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTE);
    const label = ascii(p.label) + ' ';
    doc.text(label, lx + 8, fy);
    const lw = doc.getTextWidth(label);
    doc.setFont('courier', 'bold');
    doc.setTextColor(...INK);
    const val = (Number(p.pct) * 100).toFixed(1) + '%';
    doc.text(val, lx + 8 + lw, fy);
    lx += 8 + lw + doc.getTextWidth(val) + 12;
  }

  /* ---- what was held back, named rather than dropped ---- */
  if (omitted.length) {
    fy += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...FAINT);
    // Grouped by section: the labels had their section prefix stripped for the
    // body, so "W Factory" alone would not say which W Factory line is meant.
    const bySection = [];
    for (const o of omitted) {
      const found = bySection.find(g => g.section === o.section);
      if (found) found.labels.push(o.label);
      else bySection.push({ section: o.section, labels: [o.label] });
    }
    const note = 'Nil this month, not listed above - '
      + bySection.map(g => g.section + ': ' + g.labels.join(', ')).join('; ') + '.';
    doc.text(doc.splitTextToSize(ascii(note), PCT_RIGHT - M_LEFT), M_LEFT, fy);
  }

  /* ---- page foot ---- */
  const footY = PAGE_H - M_BOTTOM;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.5);
  doc.line(M_LEFT, footY - 10, PCT_RIGHT, footY - 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...FAINT);
  doc.text(ascii(org) + ' - Management accounts, unaudited', M_LEFT, footY - 2);
  doc.text(ascii('Generated ' + generatedOn + ' - Page 1 of 1'), PCT_RIGHT, footY - 2, { align: 'right' });

  return doc;
}

/* ==========================================================================
   Comparison — two months side by side, same row wash, same paper
   ========================================================================== */

/* Six numeric columns, laid out from the right edge inwards. Each month carries
   its own share of revenue so a line reads from where to where — 25.4% to 31.9%
   — which is what makes the movement column self-explanatory. */
const C_PTS_RIGHT = PAGE_W - M_RIGHT;            // movement in share of revenue
const C_PTS_W = 36;                              // fits "-13.3%" with room to spare
const C_CHG_RIGHT = C_PTS_RIGHT - C_PTS_W - 10;  // gap keeps the delta clear of CHANGE RM
const C_CHG_W = 68;
const C_PCT_W = 32;
const C_RM_W = 62;
const C_B_PCT_RIGHT = C_CHG_RIGHT - C_CHG_W - 8;
const C_B_RIGHT = C_B_PCT_RIGHT - C_PCT_W - 3;
const C_A_PCT_RIGHT = C_B_RIGHT - C_RM_W - 8;
const C_A_RIGHT = C_A_PCT_RIGHT - C_PCT_W - 3;
/* Each month's block spans its amount and share columns; the month name is
   centred over that span so the pairing is structural, not inferred. */
const C_A_BLOCK = [C_A_RIGHT - C_RM_W, C_A_PCT_RIGHT];
const C_B_BLOCK = [C_B_RIGHT - C_RM_W, C_B_PCT_RIGHT];

/**
 * Draw a right-aligned caption prefixed with a delta.
 *
 * The standard fonts are WinAnsi and have no delta glyph — passing one through
 * ascii() would strip it and leave a stray leading space. jsPDF's Symbol font
 * does carry it, where 'D' renders as the capital delta, so the glyph comes
 * from Symbol and the rest of the caption from Helvetica.
 */
function deltaCaption(doc, rightX, y, tail, size, charSpace) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(size);
  doc.text(tail, rightX, y, { align: 'right', charSpace });
  const tailW = doc.getTextWidth(tail) + charSpace * tail.length;
  doc.setFont('symbol', 'normal');
  doc.text('D', rightX - tailW - 1, y, { align: 'right' });
  doc.setFont('helvetica', 'normal');
}

function signed(v) {
  if (v == null) return '-';
  if (v === 0) return '0.00';
  return (v > 0 ? '+' : '-') + Math.abs(v).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function signedPts(v) {
  if (v == null) return '-';
  return (v > 0 ? '+' : v < 0 ? '-' : '') + Math.abs(v).toFixed(1) + '%';
}

/**
 * Build the comparison rows. A line nil in *both* months carries nothing and is
 * held back; a line present in either month stays, so an opening or a closing
 * venue is never hidden.
 */
export function buildComparisonRows(rows, la, lb, revA, revB) {
  const out = [];
  const omitted = [];
  let section = null;
  let sectionKey = null;
  let pastFirstTotal = false;

  for (const row of rows) {
    if (row.kind === 'section') {
      section = row.label;
      sectionKey = row.key.replace(/^sec_/, '');
      pastFirstTotal = false;
      out.push({ type: 'section', label: row.label });
      continue;
    }
    const ca = la[row.key] || {};
    const cb = lb[row.key] || {};
    const a = ca.actual == null ? null : Number(ca.actual);
    const b = cb.actual == null ? null : Number(cb.actual);
    const type = classify(row);
    const bothNil = (a == null || a === 0) && (b == null || b === 0);

    if (type === 'item' && bothNil && !pastFirstTotal) {
      omitted.push({ section, label: tidyLabel(row.label, section) });
      continue;
    }
    if (type !== 'item') pastFirstTotal = true;

    const dir = direction(sectionKey, row.key);
    const d = (a == null && b == null) ? null : (b || 0) - (a || 0);
    const shareA = (a == null || !revA) ? null : a / revA;
    const shareB = (b == null || !revB) ? null : b / revB;
    const rawPts = (a == null && b == null) ? null
      : ((b || 0) / (revB || 1) - (a || 0) / (revA || 1)) * 100;

    out.push({
      type,
      label: tidyLabel(row.label, section),
      indent: row.kind === 'child',
      a, b, d,
      shareA, shareB,
      pts: rawPts == null ? null : rawPts * dir,
      effect: d == null ? null : d * dir,
    });
  }

  const pruned = out.filter((r, i) => r.type !== 'section' || (out[i + 1] && out[i + 1].type !== 'section'));
  return { rows: pruned, omitted };
}

/**
 * Draw the two-month comparison. Same paper, same wash, same rules as the
 * monthly statement — the two exports are meant to be filed together.
 */
export function drawRowWashComparison(doc, opts) {
  const { org = 'Runningmen Group', labelA = '', labelB = '', generatedOn = '' } = opts;
  const revA = opts.revenueA, revB = opts.revenueB;
  const { rows: body, omitted } = buildComparisonRows(opts.rows, opts.lookupA, opts.lookupB, revA, revB);

  /* ---- header ---- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.6);
  doc.setTextColor(...AMBER);
  doc.text(ascii(org).toUpperCase(), M_LEFT, M_TOP + 10, { charSpace: 1.2 });

  doc.setFont('times', 'normal');
  doc.setFontSize(16);
  doc.setTextColor(...INK);
  doc.text('P&L Comparison', M_LEFT, M_TOP + 28);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text(ascii(labelA + ' vs ' + labelB), C_PTS_RIGHT, M_TOP + 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(...MUTE);
  doc.text('Tint shows share of ' + ascii(labelB) + ' revenue', C_PTS_RIGHT, M_TOP + 20, { align: 'right' });
  doc.text('All figures in RM', C_PTS_RIGHT, M_TOP + 29, { align: 'right' });

  /* ---- column captions: month names over their own pair, then the sub-heads ---- */
  let y = M_TOP + 42;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.setTextColor(...MUTE);
  [[labelA, C_A_BLOCK], [labelB, C_B_BLOCK]].forEach(([label, [x0, x1]]) => {
    doc.text(ascii(label).toUpperCase(), (x0 + x1) / 2, y, { align: 'center', charSpace: 0.5 });
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.5);
    doc.line(x0, y + 2.5, x1, y + 2.5);
  });

  y += 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...FAINT);
  doc.text('RM', C_A_RIGHT, y, { align: 'right', charSpace: 0.5 });
  doc.text('% REV', C_A_PCT_RIGHT, y, { align: 'right', charSpace: 0.5 });
  doc.text('RM', C_B_RIGHT, y, { align: 'right', charSpace: 0.5 });
  doc.text('% REV', C_B_PCT_RIGHT, y, { align: 'right', charSpace: 0.5 });
  doc.text('CHANGE RM', C_CHG_RIGHT, y, { align: 'right', charSpace: 0.5 });
  deltaCaption(doc, C_PTS_RIGHT, y, ' % REV', 6, 0.5);
  y += 6;

  /* ---- fit to one page ---- */
  const footerH = 46;
  const available = PAGE_H - M_BOTTOM - footerH - y;
  const unit = fitUnit(body, available, 12, 7.2);

  /* ---- body ---- */
  for (const row of body) {
    const h = rowHeight(row.type, unit);

    if (row.type === 'section') {
      y += h;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.6);
      doc.setTextColor(...AMBER);
      doc.text(ascii(row.label).toUpperCase(), M_LEFT, y - 3.5, { charSpace: 1.1 });
      doc.setDrawColor(...RULE);
      doc.setLineWidth(0.5);
      doc.line(M_LEFT, y, C_PTS_RIGHT, y);
      continue;
    }

    const top = y;
    y += h;
    const baseline = y - h * 0.28;

    // wash: the later month's share of its own revenue
    if (row.shareB != null && Math.abs(row.shareB) > 0) {
      doc.setFillColor(...WASH);
      doc.rect(M_LEFT, top + 0.6, (C_PTS_RIGHT - M_LEFT) * Math.min(1, Math.abs(row.shareB)), h - 1.2, 'F');
    }

    if (row.type === 'total' || row.type === 'key') {
      doc.setDrawColor(...RULE); doc.setLineWidth(0.5);
      doc.line(M_LEFT, top, C_PTS_RIGHT, top);
    } else if (row.type === 'final') {
      doc.setDrawColor(...INK); doc.setLineWidth(0.5);
      doc.line(M_LEFT, top, C_PTS_RIGHT, top);
      doc.line(M_LEFT, top + 1.8, C_PTS_RIGHT, top + 1.8);
    }

    let labelX = M_LEFT + (row.indent ? 9 : 0);
    if (row.type === 'key') {
      doc.setFillColor(...AMBER);
      doc.rect(M_LEFT, top + 2.4, 1.8, h - 4.8, 'F');
      labelX = M_LEFT + 5;
    }

    const bold = row.type !== 'item';
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(row.type === 'final' ? 8.6 : 7.8);
    doc.setTextColor(...INK);
    doc.text(ascii(row.label), labelX, baseline);

    // Each month: its amount, then its share of that month's own revenue. The
    // shares stay neutral grey — only the movement columns carry semantic colour,
    // so the eye still lands on what changed.
    [[row.a, row.shareA, C_A_RIGHT, C_A_PCT_RIGHT],
     [row.b, row.shareB, C_B_RIGHT, C_B_PCT_RIGHT]].forEach(([v, share, xRm, xPct]) => {
      doc.setFont('courier', bold ? 'bold' : 'normal');
      doc.setFontSize(row.type === 'final' ? 7.8 : 7.1);
      doc.setTextColor(...(v != null && v < 0 ? BAD : INK));
      doc.text(money(v), xRm, baseline, { align: 'right' });

      doc.setFont('courier', 'normal');
      doc.setFontSize(6.3);
      doc.setTextColor(...(share == null ? FAINT : MUTE));
      doc.text(pctText(share), xPct, baseline, { align: 'right' });
    });

    // Change and points share one colour: green when the movement helped profit,
    // red when it hurt. A cost that rose is red however its arithmetic sign reads.
    const tone = row.effect == null || row.effect === 0 ? MUTE : row.effect > 0 ? GOOD : BAD;
    doc.setFont('courier', 'bold');
    doc.setFontSize(row.type === 'final' ? 7.8 : 7.1);
    doc.setTextColor(...tone);
    doc.text(signed(row.d), C_CHG_RIGHT, baseline, { align: 'right' });

    doc.setFont('courier', 'normal');
    doc.setFontSize(6.6);
    doc.setTextColor(...tone);
    doc.text(signedPts(row.pts), C_PTS_RIGHT, baseline, { align: 'right' });
  }

  /* ---- footer ---- */
  let fy = PAGE_H - M_BOTTOM - footerH + 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...FAINT);
  const notes = ['Change RM is ' + ascii(labelB) + ' minus ' + ascii(labelA)
    + ', coloured by its effect on profit - a cost that rose is red whatever its sign. '
    + 'The last column is the movement in this line\'s share of revenue, in percentage points, '
    + 'signed so better is positive: food supply going from 25.4% to 31.9% of revenue reads -6.5%.'];
  if (omitted.length) {
    const bySection = [];
    for (const o of omitted) {
      const g = bySection.find(x => x.section === o.section);
      if (g) g.labels.push(o.label); else bySection.push({ section: o.section, labels: [o.label] });
    }
    notes.push('Nil in both months, not listed above - '
      + bySection.map(g => g.section + ': ' + g.labels.join(', ')).join('; ') + '.');
  }
  doc.text(doc.splitTextToSize(ascii(notes.join(' ')), C_PTS_RIGHT - M_LEFT), M_LEFT, fy);

  const footY = PAGE_H - M_BOTTOM;
  doc.setDrawColor(...HAIR); doc.setLineWidth(0.5);
  doc.line(M_LEFT, footY - 10, C_PTS_RIGHT, footY - 10);
  doc.setTextColor(...FAINT);
  doc.setFontSize(6);
  doc.text(ascii(org) + ' - Management accounts, unaudited', M_LEFT, footY - 2);
  doc.text(ascii('Generated ' + generatedOn + ' - Page 1 of 1'), C_PTS_RIGHT, footY - 2, { align: 'right' });

  return doc;
}

/* ==========================================================================
   Period grid — many columns (a quarter, a year to date, a full year)
   ========================================================================== */

const L_PAGE_W = 841.89;   // A4 landscape
const L_PAGE_H = 595.28;
const L_LABEL_W = 168;

/** Whole ringgit, for grids too wide to afford two decimal places. */
function moneyRound(v) {
  return accounting0(v == null ? null : Number(v), '-');
}

/**
 * Rows for the grid: a detail line nil in *every* column carries nothing and is
 * held back. A line with a value in even one period stays, so a venue that only
 * traded in March is never hidden.
 */
export function buildGridRows(rows, lookups) {
  const out = [];
  const omitted = [];
  let section = null;
  let pastFirstTotal = false;

  for (const row of rows) {
    if (row.kind === 'section') {
      section = row.label;
      pastFirstTotal = false;
      out.push({ type: 'section', label: row.label });
      continue;
    }
    const type = classify(row);
    const values = lookups.map(l => {
      const v = (l[row.key] || {}).actual;
      return v == null ? null : Number(v);
    });
    const allNil = values.every(v => v == null || v === 0);

    if (type === 'item' && allNil && !pastFirstTotal) {
      omitted.push({ section, label: tidyLabel(row.label, section) });
      continue;
    }
    if (type !== 'item') pastFirstTotal = true;

    out.push({ type, label: tidyLabel(row.label, section), values, indent: row.kind === 'child' });
  }

  const pruned = out.filter((r, i) => r.type !== 'section' || (out[i + 1] && out[i + 1].type !== 'section'));
  return { rows: pruned, omitted };
}

/**
 * Draw a multi-period grid. Landscape, one page, same hierarchy and palette as
 * the single-month statement so the whole export set reads as one family.
 *
 * @param opts.columns  [{ label, isTotal }] in display order
 * @param opts.lookups  buildColLookup() per column, same order
 */
export function drawPeriodGrid(doc, opts) {
  const { org = 'Runningmen Group', title = 'Profit & Loss Statement',
    periodLabel = '', generatedOn = '', columns = [], lookups = [] } = opts;
  const { rows: body, omitted } = buildGridRows(opts.rows, lookups);

  const n = columns.length;
  const totalIdx = columns.findIndex(c => c.isTotal);
  const washIdx = totalIdx >= 0 ? totalIdx : n - 1;      // weight comes from the total column
  const washRev = Number((((lookups[washIdx] || {}).total_revenue) || {}).actual) || 0;

  const right = L_PAGE_W - M_RIGHT;
  const pctW = totalIdx >= 0 ? 30 : 0;                  // % of revenue, total column only
  const avail = right - M_LEFT - L_LABEL_W - pctW - 8;
  const colW = avail / n;

  // Two decimals cost four characters a column; past six columns the page cannot
  // afford them, and at a year's scale the sen are noise anyway.
  const round = n > 6;
  const fmt = round ? moneyRound : money;
  const widest = round ? 11 : 14;                       // "(1,030,651)" / "(1,030,650.55)"
  const valSize = Math.max(4.8, Math.min(7.4, (colW - 5) / (widest * 0.6)));

  const colRight = (i) => M_LEFT + L_LABEL_W + colW * (i + 1) - 3;
  const pctRight = right;

  /* ---- header ---- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.6);
  doc.setTextColor(...AMBER);
  doc.text(ascii(org).toUpperCase(), M_LEFT, M_TOP + 10, { charSpace: 1.2 });

  doc.setFont('times', 'normal');
  doc.setFontSize(16);
  doc.setTextColor(...INK);
  doc.text(ascii(title), M_LEFT, M_TOP + 28);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text(ascii(periodLabel), right, M_TOP + 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(...MUTE);
  doc.text('All figures in RM' + (round ? ', rounded' : ''), right, M_TOP + 20, { align: 'right' });
  if (totalIdx >= 0) doc.text('Tint shows share of ' + ascii(columns[totalIdx].label) + ' revenue', right, M_TOP + 29, { align: 'right' });

  /* ---- captions ---- */
  let y = M_TOP + 44;
  doc.setFontSize(5.8);
  doc.setTextColor(...FAINT);
  columns.forEach((c, i) => {
    doc.setFont('helvetica', c.isTotal ? 'bold' : 'normal');
    doc.setTextColor(...(c.isTotal ? MUTE : FAINT));
    doc.text(ascii(c.label).toUpperCase(), colRight(i), y, { align: 'right', charSpace: 0.4 });
  });
  if (totalIdx >= 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...FAINT);
    doc.text('% REV', pctRight, y, { align: 'right', charSpace: 0.4 });
  }
  y += 5;

  /* ---- fit ---- */
  const footerH = 40;
  const available = L_PAGE_H - M_BOTTOM - footerH - y;
  const unit = fitUnit(body, available, 11, 6.4);

  /* ---- body ---- */
  for (const row of body) {
    const h = rowHeight(row.type, unit);

    if (row.type === 'section') {
      y += h;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.4);
      doc.setTextColor(...AMBER);
      doc.text(ascii(row.label).toUpperCase(), M_LEFT, y - 3, { charSpace: 1.1 });
      doc.setDrawColor(...RULE); doc.setLineWidth(0.5);
      doc.line(M_LEFT, y, right, y);
      continue;
    }

    const top = y;
    y += h;
    const baseline = y - h * 0.28;

    const washVal = row.values[washIdx];
    const share = (washRev && washVal != null) ? washVal / washRev : null;
    if (share != null && Math.abs(share) > 0) {
      doc.setFillColor(...WASH);
      doc.rect(M_LEFT, top + 0.5, (right - M_LEFT) * Math.min(1, Math.abs(share)), h - 1, 'F');
    }

    if (row.type === 'total' || row.type === 'key') {
      doc.setDrawColor(...RULE); doc.setLineWidth(0.5);
      doc.line(M_LEFT, top, right, top);
    } else if (row.type === 'final') {
      doc.setDrawColor(...INK); doc.setLineWidth(0.5);
      doc.line(M_LEFT, top, right, top);
      doc.line(M_LEFT, top + 1.6, right, top + 1.6);
    }

    let labelX = M_LEFT + (row.indent ? 8 : 0);
    if (row.type === 'key') {
      doc.setFillColor(...AMBER);
      doc.rect(M_LEFT, top + 2, 1.8, h - 4, 'F');
      labelX = M_LEFT + 5;
    }

    const bold = row.type !== 'item';
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(Math.min(7.6, unit * 0.66));
    doc.setTextColor(...INK);
    doc.text(ascii(row.label), labelX, baseline);

    row.values.forEach((v, i) => {
      doc.setFont('courier', (bold || columns[i].isTotal) ? 'bold' : 'normal');
      doc.setFontSize(valSize);
      doc.setTextColor(...(v != null && v < 0 ? BAD : INK));
      doc.text(fmt(v), colRight(i), baseline, { align: 'right' });
    });

    if (totalIdx >= 0) {
      doc.setFont('courier', 'normal');
      doc.setFontSize(Math.max(4.6, valSize - 0.8));
      doc.setTextColor(...MUTE);
      doc.text(pctText(share), pctRight, baseline, { align: 'right' });
    }
  }

  /* ---- footer ---- */
  let fy = L_PAGE_H - M_BOTTOM - footerH + 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.8);
  doc.setTextColor(...FAINT);
  if (omitted.length) {
    const bySection = [];
    for (const o of omitted) {
      const g = bySection.find(x => x.section === o.section);
      if (g) g.labels.push(o.label); else bySection.push({ section: o.section, labels: [o.label] });
    }
    const note = 'Nil in every period, not listed above - '
      + bySection.map(g => g.section + ': ' + g.labels.join(', ')).join('; ') + '.';
    doc.text(doc.splitTextToSize(ascii(note), right - M_LEFT), M_LEFT, fy);
  }

  const footY = L_PAGE_H - M_BOTTOM;
  doc.setDrawColor(...HAIR); doc.setLineWidth(0.5);
  doc.line(M_LEFT, footY - 10, right, footY - 10);
  doc.setTextColor(...FAINT);
  doc.setFontSize(5.8);
  doc.text(ascii(org) + ' - Management accounts, unaudited', M_LEFT, footY - 2);
  doc.text(ascii('Generated ' + generatedOn + ' - Page 1 of 1'), right, footY - 2, { align: 'right' });

  return doc;
}

export const __test__ = { classify, rowHeight, money, pctText, ascii, direction };
